import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api from '../api';

const MESES_FULL = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_SHORT = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const PROCESOS = [
  { id: 'compras', label: 'Compras', color: 'green' },
  { id: 'ventas', label: 'Ventas', color: 'blue' },
  { id: 'honorarios', label: 'Honorarios', color: 'purple' },
  { id: 'bte', label: 'BTE', color: 'teal' },
  { id: 'centralizacion', label: 'Centralización', color: 'amber' },
];

function fmtCLP(val) {
  if (!val && val !== 0) return '—';
  return '$' + Number(val).toLocaleString('es-CL');
}
function parseMonto(v) {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  return parseInt(String(v).replace(/\./g, '').replace(/[^0-9-]/g, ''), 10) || 0;
}
function parseDocs(v) {
  if (typeof v === 'number') return v;
  return parseInt(v, 10) || 0;
}

function getEstadoMes(d) {
  const logs = [d.logC, d.logV, d.logCent, d.logHono, d.logBte].filter(Boolean);
  if (logs.length === 0) return 'vacio';
  if (logs.some(l => l.estado === 'error')) return 'error';
  if (logs.some(l => l.estado === 'en_progreso')) return 'en_progreso';
  if (logs.some(l => l.estado === 'en_cola')) return 'en_cola';
  if (logs.some(l => l.estado === 'completado')) return 'completado';
  return 'parcial';
}

const ESTADO_MES_STYLES = {
  vacio: 'bg-gray-50 text-gray-400 border-gray-200',
  error: 'bg-red-50 text-red-700 border-red-300 ring-1 ring-red-200',
  en_progreso: 'bg-yellow-50 text-yellow-700 border-yellow-300 ring-1 ring-yellow-200',
  en_cola: 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-200',
  completado: 'bg-green-50 text-green-700 border-green-300',
  parcial: 'bg-slate-50 text-slate-600 border-slate-300',
};

const ESTADO_MES_DOT = {
  vacio: 'bg-gray-300',
  error: 'bg-red-500',
  en_progreso: 'bg-yellow-500 animate-pulse',
  en_cola: 'bg-blue-500',
  completado: 'bg-green-500',
  parcial: 'bg-slate-400',
};

function EstadoBadge({ estado, ok, sinDatos, logId, onCambiarEstado }) {
  const getBadge = () => {
    if (estado === 'en_cola') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">⏳ En Cola</span>;
    if (estado === 'en_progreso') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">⟳ Procesando</span>;
    if (estado === 'completado') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">✓ OK</span>;
    if (estado === 'error') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">✗ Error</span>;

    if (sinDatos) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Sin datos</span>;
    if (ok === true)  return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">✓ OK</span>;
    if (ok === false) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">✗ Error</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">—</span>;
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {getBadge()}
      {logId && onCambiarEstado && (
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) onCambiarEstado(logId, e.target.value);
          }}
          className="text-xs bg-transparent border-none text-gray-400 hover:text-gray-700 cursor-pointer appearance-none text-center p-0 outline-none"
          title="Forzar cambio de estado"
        >
          <option value="">⚙️</option>
          <option value="en_cola">Forzar: En cola</option>
          <option value="en_progreso">Forzar: En progreso</option>
          <option value="completado">Forzar: Completado</option>
          <option value="error">Forzar: Error</option>
          <option value="cancelado">Forzar: Cancelado</option>
        </select>
      )}
    </div>
  );
}

function TablaResumenSII({ resumenSii, logData }) {
  let rows = resumenSii || [];
  if (logData && logData.tipoDocumento === 'honorarios' && logData.honorarios?.totalBruto) {
    if (rows.length === 0) {
      rows = [{
        tipoDocumento: 'Honorarios',
        totalBruto: logData.honorarios.totalBruto,
        totalRetenido: logData.honorarios.totalRetenido,
        totalPagado: logData.honorarios.totalPagado
      }];
    }
  }

  if (!rows || rows.length === 0) return <p className="text-xs text-gray-400 italic py-3 text-center">Sin datos de resumen SII</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-xs whitespace-nowrap">
        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide font-semibold border-b">
          <tr>
            <th className="px-3 py-2 text-left">Tipo de Documento</th>
            {logData?.tipoDocumento === 'honorarios' ? (
              <>
                <th className="px-3 py-2 text-right">Brutos</th>
                <th className="px-3 py-2 text-right">Retenido</th>
                <th className="px-3 py-2 text-right font-bold text-gray-700">Pagado</th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 text-right">Cant.</th>
                <th className="px-3 py-2 text-right">M. Exento</th>
                <th className="px-3 py-2 text-right">M. Neto</th>
                <th className="px-3 py-2 text-right">M. IVA</th>
                <th className="px-3 py-2 text-right font-bold text-gray-700">Total</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => {
            const esBoleta = row.tipoDocumento && (row.tipoDocumento.toUpperCase().includes('BOLETA') || row.tipoDocumento.toUpperCase().includes('COMPROBANTE PAGO'));
            return (
              <tr key={i} className={`hover:bg-gray-50 transition-colors ${esBoleta ? 'bg-indigo-50/50' : ''}`}>
                <td className="px-3 py-2 text-gray-700 font-medium">
                  {esBoleta && <span className="inline-block mr-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 align-middle" />}
                  {row.tipoDocumento}
                </td>
                {logData?.tipoDocumento === 'honorarios' ? (
                  <>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.totalBruto || row.montoNeto || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.totalRetenido || row.montoIva || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-semibold font-mono">{row.totalPagado || row.montoTotal || '0'}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right text-gray-600 font-mono">{row.totalDocumentos || row.cantidadDocumentos || 0}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoExento || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoNeto || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoIva || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-semibold font-mono">{row.montoTotal || '0'}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function fmtFecha(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  return isNaN(d.getTime()) ? null : d.toLocaleString('es-CL');
}

function MiniProcesosMes({ d }) {
  const procs = [
    { key: 'C', log: d.logC },
    { key: 'V', log: d.logV },
    { key: 'H', log: d.logHono },
    { key: 'B', log: d.logBte },
  ];
  if (!procs.some(p => p.log)) return null;

  const estiloLog = (log) => {
    if (!log) return 'bg-gray-100 text-gray-300';
    if (log.estado === 'completado') return 'bg-green-200 text-green-800';
    if (log.estado === 'error') return 'bg-red-200 text-red-800';
    if (log.estado === 'en_progreso') return 'bg-yellow-200 text-yellow-800';
    if (log.estado === 'en_cola') return 'bg-blue-200 text-blue-800';
    return 'bg-slate-200 text-slate-600';
  };

  return (
    <div className="flex gap-0.5 mt-0.5">
      {procs.map(p => (
        <span
          key={p.key}
          title={p.log ? `${p.key}: ${p.log.estado || 'registrado'}` : `${p.key}: sin datos`}
          className={`text-[8px] w-3.5 h-3.5 rounded flex items-center justify-center font-bold leading-none ${estiloLog(p.log)}`}
        >
          {p.key}
        </span>
      ))}
    </div>
  );
}

function PanelProceso({ tipo, log, color, mesNum, descargandoArchivo, onDescargar, onReejecutar, onCambiarEstado, logCompras, logVentas, logCent }) {
  const colorMap = {
    green: { text: 'text-green-700', dot: 'bg-green-500' },
    blue: { text: 'text-blue-700', dot: 'bg-blue-500' },
    purple: { text: 'text-purple-700', dot: 'bg-purple-500' },
    teal: { text: 'text-teal-700', dot: 'bg-teal-500' },
    amber: { text: 'text-amber-700', dot: 'bg-amber-500' },
  };
  const { text: textColor, dot: dotColor } = colorMap[color] || { text: 'text-gray-700', dot: 'bg-gray-500' };

  const label = tipo === 'centralizacion' ? 'Centralización' : tipo === 'bte' ? 'BTE' : tipo.charAt(0).toUpperCase() + tipo.slice(1);

  const centLog = tipo === 'centralizacion'
    ? (logCent || (logCompras?.centralizacionInputsoft ? logCompras : logVentas))
    : log;

  const displayLog = tipo === 'centralizacion' ? centLog : log;

  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
          <h4 className={`text-sm font-semibold ${textColor}`}>{label}</h4>
          {(() => {
            const fecha = fmtFecha(displayLog?.fechaInicio);
            return fecha ? <span className="text-xs text-gray-400">{fecha}</span> : null;
          })()}
        </div>
        <div className="flex items-center gap-2">
          {displayLog && (
            <EstadoBadge
              estado={displayLog.estado}
              ok={tipo === 'honorarios' ? displayLog.honorarios?.ok
                : tipo === 'bte' ? displayLog.bte?.ok
                : tipo === 'centralizacion' ? displayLog.centralizacionInputsoft?.ok
                : displayLog.descargaSii?.sinDatos ? undefined : displayLog.subidaInputsoft?.ok}
              sinDatos={tipo === 'honorarios' ? displayLog.honorarios?.sinDatos
                : tipo === 'bte' ? displayLog.bte?.sinDatos
                : displayLog.descargaSii?.sinDatos}
              logId={displayLog._id}
              onCambiarEstado={(id, est) => onCambiarEstado(id, est, tipo, mesNum)}
            />
          )}
          {(!displayLog || (displayLog.estado !== 'en_progreso' && displayLog.estado !== 'en_cola')) && (
            <button
              onClick={() => onReejecutar(mesNum, tipo)}
              className="px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-xs uppercase font-bold tracking-wider shadow-sm transition-colors"
            >
              ▶ {displayLog ? 'Re-ejecutar' : 'Ejecutar'}
            </button>
          )}
        </div>
      </div>

      {!displayLog ? (
        <p className="text-sm text-gray-400 italic text-center py-8">Sin registro de pipeline para este proceso</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              ['descargaSii', 'SII'],
              ['procesamientoCsv', 'CSV'],
              ['subidaInputsoft', 'Inputsoft'],
              ['ingresoBoletas', 'Boletas'],
              ['centralizacionInputsoft', 'Centralización']
            ].map(([key, etapaLabel]) => {
              const etapa = displayLog[key];
              if (!etapa) return null;
              return (
                <span key={key} className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  etapa.ok ? 'bg-green-100 text-green-700' :
                  etapa.ok === false ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {etapa.ok ? '✓' : etapa.ok === false ? '✗' : '…'} {etapaLabel}
                  {etapa.sinDatos ? ' (sin datos)' : ''}
                  {etapa.filasProcesadas != null ? ` (${etapa.filasProcesadas} f.)` : ''}
                </span>
              );
            })}
          </div>

          <TablaResumenSII resumenSii={displayLog.resumenSii} logData={displayLog} />

          {(displayLog.subidaInputsoft?.error || displayLog.subidaInputsoft?.detallesError?.length > 0 || displayLog.ingresoBoletas?.error) && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3">
              <h5 className="text-xs font-semibold text-orange-600 mb-2 flex items-center gap-1">
                ⚠ Detalles del Error / Rechazo
              </h5>

              {displayLog.subidaInputsoft?.error && (
                <div className="text-xs text-red-700 bg-red-50 p-2 border border-red-200 rounded mb-2">
                  <span className="font-semibold">Subida CSV:</span> {displayLog.subidaInputsoft.error}
                </div>
              )}

              {displayLog.ingresoBoletas?.error && (
                <div className="text-xs text-red-700 bg-red-50 p-2 border border-red-200 rounded mb-2">
                  <span className="font-semibold">Ingreso Boletas:</span> {displayLog.ingresoBoletas.error}
                </div>
              )}

              {displayLog.subidaInputsoft?.detallesError?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-orange-200 rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-orange-50 text-orange-700 sticky top-0">
                      <tr>
                        <th className="p-2 text-left font-medium">Tipo</th>
                        <th className="p-2 text-left font-medium">Folio</th>
                        <th className="p-2 text-left font-medium">Motivo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-100">
                      {displayLog.subidaInputsoft.detallesError.map((e, i) => (
                        <tr key={i}>
                          <td className="p-2 text-gray-500">{e.tipo || '—'}</td>
                          <td className="p-2 font-mono text-gray-700">{e.folio}</td>
                          <td className="p-2 text-orange-700">{e.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {displayLog.centralizacionInputsoft?.resultados?.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-3">
              <h5 className="text-xs font-semibold text-blue-600 mb-2">
                ℹ Resultados Centralización
              </h5>
              <div className="max-h-32 overflow-y-auto border border-blue-200 rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-blue-50 text-blue-700 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Tipo</th>
                      <th className="p-2 text-right font-medium">Debe</th>
                      <th className="p-2 text-right font-medium">Haber</th>
                      <th className="p-2 text-left font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    {displayLog.centralizacionInputsoft.resultados.map((r, i) => (
                      <tr key={i}>
                        <td className="p-2 text-gray-700 font-medium">{
                          r.tipoDocumento === '1' ? 'Ventas (1)' :
                          r.tipoDocumento === '2' ? 'Compras (2)' :
                          r.tipoDocumento === '3' ? 'Boletas (3)' :
                          r.tipoDocumento === '4' ? 'Honorarios (4)' : r.tipoDocumento
                        }</td>
                        <td className="p-2 text-right font-mono text-gray-600">{r.debe != null ? r.debe.toLocaleString('es-CL') : '—'}</td>
                        <td className="p-2 text-right font-mono text-gray-600">{r.haber != null ? r.haber.toLocaleString('es-CL') : '—'}</td>
                        <td className="p-2 text-gray-600">
                          {r.ok ? <span className="text-green-600">✓ {r.mensaje || 'OK'}</span> : <span className="text-red-600">✗ {r.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {displayLog.descargaSii?.archivo && (() => {
              const fname = displayLog.descargaSii.archivo.split(/[/\\]/).pop();
              return (
                <button onClick={() => onDescargar(displayLog._id, fname)}
                  disabled={descargandoArchivo === fname}
                  className="inline-flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-50 transition-colors">
                  📄 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Original SII'}
                </button>
              );
            })()}
            {displayLog.procesamientoCsv?.archivo && (() => {
              const fname = displayLog.procesamientoCsv.archivo.split(/[/\\]/).pop();
              return (
                <button onClick={() => onDescargar(displayLog._id, fname)}
                  disabled={descargandoArchivo === fname}
                  className="inline-flex items-center gap-1.5 text-xs border border-blue-200 rounded-lg px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 disabled:opacity-50 transition-colors">
                  📊 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Procesado'}
                </button>
              );
            })()}
            {(displayLog.subidaInputsoft?.error || displayLog.subidaInputsoft?.ok === false || displayLog.ingresoBoletas?.error || displayLog.ingresoBoletas?.ok === false) && (
              <>
                <button onClick={() => onDescargar(displayLog._id, 'error-subida.png')}
                  disabled={descargandoArchivo === 'error-subida.png'}
                  className="inline-flex items-center gap-1.5 text-xs border border-orange-200 rounded-lg px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition-colors">
                  🖼 {descargandoArchivo === 'error-subida.png' ? 'Descargando...' : 'Captura Error'}
                </button>
                <button onClick={() => onDescargar(displayLog._id, 'error-subida.html')}
                  disabled={descargandoArchivo === 'error-subida.html'}
                  className="inline-flex items-center gap-1.5 text-xs border border-orange-200 rounded-lg px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition-colors">
                  🌐 {descargandoArchivo === 'error-subida.html' ? 'Descargando...' : 'HTML Error'}
                </button>
              </>
            )}
          </div>

          <details className="group">
            <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 select-none">
              <span className="transition-transform group-open:rotate-90">▶</span> Ver log de ejecución
            </summary>
            <div className="mt-2 p-3 bg-gray-900 text-gray-300 rounded-lg text-xs font-mono max-h-56 overflow-y-auto whitespace-pre-wrap leading-relaxed">
              {displayLog.logEntries?.join('\n') || 'Sin logs'}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function SelectorMeses({ datosMes, mesSeleccionado, onSelect }) {
  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Seleccionar mes</p>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> OK</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Error</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Proceso</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Cola</span>
          <span className="hidden sm:inline text-gray-300">|</span>
          <span className="hidden sm:inline">C V H B = procesos</span>
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
        {datosMes.map(d => {
          const estado = getEstadoMes(d);
          const activo = mesSeleccionado === d.mesNum;
          return (
            <button
              key={d.mesNum}
              onClick={() => onSelect(d.mesNum)}
              className={`relative flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-xs font-semibold transition-all ${
                activo
                  ? 'ring-2 ring-indigo-500 ring-offset-1 shadow-md ' + ESTADO_MES_STYLES[estado]
                  : ESTADO_MES_STYLES[estado] + ' hover:shadow-sm'
              }`}
            >
              <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${ESTADO_MES_DOT[estado]}`} />
              <span>{d.nombre}</span>
              <MiniProcesosMes d={d} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResumenMes({ d }) {
  const items = [
    { label: 'Compras', value: d.totCompras, docs: d.docsC, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
    { label: 'Ventas', value: d.totVentas, docs: d.docsV, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
    { label: 'Honorarios', value: d.totHono, color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
    { label: 'BTE', value: d.totBte, color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map(item => (
        <div key={item.label} className={`rounded-lg border p-3 ${item.bg}`}>
          <div className="text-xs text-gray-500 font-medium">{item.label}</div>
          <div className={`text-lg font-bold ${item.color}`}>{fmtCLP(item.value)}</div>
          {item.docs > 0 && <div className="text-[10px] text-gray-400 mt-0.5">{item.docs} documentos</div>}
        </div>
      ))}
    </div>
  );
}

function DetalleMes({ d, descargandoArchivo, onDescargar, onReejecutar, onCambiarEstado }) {
  const [procesoActivo, setProcesoActivo] = useState('compras');

  const logsMap = {
    compras: d.logC,
    ventas: d.logV,
    honorarios: d.logHono,
    bte: d.logBte,
    centralizacion: d.logCent,
  };

  const procesoInfo = PROCESOS.find(p => p.id === procesoActivo);

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-base font-bold text-gray-800">{MESES_FULL[d.mesNum]} {d.mesNum}</h3>
          <EstadoBadge
            estado={getEstadoMes(d) === 'vacio' ? undefined : getEstadoMes(d) === 'completado' ? 'completado' : getEstadoMes(d) === 'error' ? 'error' : getEstadoMes(d) === 'en_progreso' ? 'en_progreso' : getEstadoMes(d) === 'en_cola' ? 'en_cola' : undefined}
          />
        </div>
        <ResumenMes d={d} />
      </div>

      <div className="flex gap-0.5 border-b overflow-x-auto bg-white rounded-t-xl px-2 pt-2">
        {PROCESOS.map(p => {
          const log = logsMap[p.id];
          const tieneLog = !!log || (p.id === 'centralizacion' && (d.logC?.centralizacionInputsoft || d.logV?.centralizacionInputsoft));
          return (
            <button
              key={p.id}
              onClick={() => setProcesoActivo(p.id)}
              className={`px-3 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                procesoActivo === p.id
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.label}
              {log?.estado === 'completado' && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
              {log?.estado === 'error' && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
              {log?.estado === 'en_progreso' && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />}
              {log?.estado === 'en_cola' && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
              {!tieneLog && <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />}
            </button>
          );
        })}
      </div>

      <PanelProceso
        tipo={procesoActivo}
        log={logsMap[procesoActivo]}
        color={procesoInfo?.color || 'gray'}
        mesNum={d.mesNum}
        logCompras={d.logC}
        logVentas={d.logV}
        logCent={d.logCent}
        descargandoArchivo={descargandoArchivo}
        onDescargar={onDescargar}
        onReejecutar={onReejecutar}
        onCambiarEstado={onCambiarEstado}
      />
    </div>
  );
}

export default function DashboardEmpresaContabilidad({ empresa, anio: anioInicial, onClose }) {
  const [anio, setAnio] = useState(anioInicial || new Date().getFullYear());
  const [logs, setLogs] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargandoArchivo, setDescargandoArchivo] = useState('');
  const [tabActiva, setTabActiva] = useState('resumen');
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);

  const [mesesMulti, setMesesMulti] = useState([]);
  const [chkCompras, setChkCompras] = useState(true);
  const [chkVentas, setChkVentas] = useState(true);
  const [chkHonorarios, setChkHonorarios] = useState(false);
  const [chkBte, setChkBte] = useState(false);
  const [chkCentralizacion, setChkCentralizacion] = useState(false);
  const [chkSoloFetch, setChkSoloFetch] = useState(false);
  const [ejecutandoMulti, setEjecutandoMulti] = useState(false);

  const cargarDatos = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const id = (empresa._id || empresa).toString();
      const [logsRes, estadosRes] = await Promise.all([
        api.get('/contabilidad/logs', { params: { clienteId: id, anio, _t: Date.now() } }),
        api.get('/contabilidad/estados', { params: { clienteId: id, anio, _t: Date.now() } })
      ]);
      setLogs(logsRes.data || []);
      setEstados(estadosRes.data || []);
    } catch (err) {
      console.error(err);
    }
    if (!silencioso) setLoading(false);
  }, [empresa, anio]);

  useEffect(() => {
    cargarDatos();
    const intervalId = setInterval(() => {
      cargarDatos(true);
    }, 3000);
    return () => clearInterval(intervalId);
  }, [cargarDatos]);

  const reejecutarProceso = async (mesNum, tipo) => {
    toast.info(`Iniciando proceso...`);
    try {
      await api.post('/contabilidad/ejecutar', {
        clienteId: empresa._id, mes: mesNum, anio, tipoDocumento: tipo, soloFetch: chkSoloFetch
      });
      toast.success('Proceso re-encolado exitosamente');
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo iniciar el proceso');
    }
  };

  const ejecutarSeleccion = async () => {
    const tipos = [];
    if (chkCompras) tipos.push('compras');
    if (chkVentas) tipos.push('ventas');
    if (chkHonorarios) tipos.push('honorarios');
    if (chkBte) tipos.push('bte');
    if (chkCentralizacion && !chkCompras && !chkVentas) tipos.push('centralizacion');

    if (tipos.length === 0 || mesesMulti.length === 0) return;

    setEjecutandoMulti(true);
    toast.info(`Iniciando multi-mes para ${mesesMulti.length} meses...`);

    for (const t of tipos) {
      try {
        await api.post('/contabilidad/ejecutar-multimes', {
          clienteId: empresa._id,
          anio,
          meses: mesesMulti,
          tipoDocumento: t,
          soloFetch: chkSoloFetch
        });
      } catch (err) {
        console.error(`Error Multi-mes seleccion ${t}:`, err);
      }
    }

    toast.success('Lote encolado exitosamente');
    cargarDatos();
    setEjecutandoMulti(false);
  };

  const ejecutarTodoMulti = async () => {
    if (mesesMulti.length === 0) return;
    setChkCompras(true); setChkVentas(true); setChkHonorarios(true); setChkBte(true); setChkCentralizacion(true);
    setEjecutandoMulti(true);
    toast.info(`Iniciando TODOS los procesos para ${mesesMulti.length} meses...`);
    const tiposTotales = ['compras', 'ventas', 'honorarios', 'bte'];
    for (const t of tiposTotales) {
      try {
        await api.post('/contabilidad/ejecutar-multimes', {
          clienteId: empresa._id, anio, meses: mesesMulti, tipoDocumento: t, soloFetch: chkSoloFetch
        });
      } catch (err) {
        console.error(`Error Multi-mes todo ${t}:`, err);
      }
    }
    toast.success('Lote completo encolado exitosamente');
    cargarDatos();
    setEjecutandoMulti(false);
  };

  const cambiarEstadoLog = async (logId, nuevoEstado, tipoDocumento, mesNum) => {
    try {
      await api.put(`/contabilidad/logs/${logId}/estado`, {
        estado: nuevoEstado,
        clienteId: (empresa._id || empresa).toString(),
        anio,
        mes: mesNum,
        tipoDocumento
      });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      cargarDatos(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  const descargarArchivo = async (logId, fileName) => {
    if (!logId || !fileName) return;
    setDescargandoArchivo(fileName);
    try {
      const response = await api.get(
        `/contabilidad/logs/${logId}/archivos/${encodeURIComponent(fileName)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo descargar el archivo');
    } finally {
      setDescargandoArchivo('');
    }
  };

  const anioActual = new Date().getFullYear();
  const años = Array.from({ length: 6 }, (_, i) => anioActual - i);

  const datosMes = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const mesNum = i + 1;
    let logC = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'compras');
    let logV = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'ventas');
    let logCent = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'centralizacion');
    let logHono = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'honorarios');
    let logBte = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'bte');

    const estadoDoc = estados.find(e => e.mes === mesNum);

    if (estadoDoc?.compras?.estado) {
      if (logC) logC.estado = estadoDoc.compras.estado;
      else logC = { estado: estadoDoc.compras.estado, _id: estadoDoc.compras.ultimoLogId };
    }
    if (estadoDoc?.ventas?.estado) {
      if (logV) logV.estado = estadoDoc.ventas.estado;
      else logV = { estado: estadoDoc.ventas.estado, _id: estadoDoc.ventas.ultimoLogId };
    }
    if (estadoDoc?.honorarios?.estado) {
      if (logHono) logHono.estado = estadoDoc.honorarios.estado;
      else logHono = { estado: estadoDoc.honorarios.estado, _id: estadoDoc.honorarios.ultimoLogId };
    }
    if (estadoDoc?.bte?.estado) {
      if (logBte) logBte.estado = estadoDoc.bte.estado;
      else logBte = { estado: estadoDoc.bte.estado, _id: estadoDoc.bte.ultimoLogId };
    }
    if (estadoDoc?.centralizacion?.estado) {
      if (logCent) logCent.estado = estadoDoc.centralizacion.estado;
      else logCent = { estado: estadoDoc.centralizacion.estado, _id: estadoDoc.centralizacion.ultimoLogId };
    }

    const totCompras = (logC?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const totVentas = (logV?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const totHono = logHono?.honorarios?.totalBruto || 0;
    const totBte = logBte?.bte?.totalBruto || 0;
    const docsC = (logC?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    const docsV = (logV?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    return { mesNum, nombre: MESES_SHORT[mesNum], totCompras, totVentas, totHono, totBte, docsC, docsV, logC, logV, logCent, logHono, logBte };
  }), [logs, estados]);

  const mesActual = datosMes.find(d => d.mesNum === mesSeleccionado) || datosMes[0];

  const totalVentasAnio = datosMes.reduce((s, d) => s + d.totVentas, 0);
  const totalComprasAnio = datosMes.reduce((s, d) => s + d.totCompras, 0);
  const totalHonoAnio = datosMes.reduce((s, d) => s + d.totHono, 0);
  const totalBteAnio = datosMes.reduce((s, d) => s + d.totBte, 0);
  const mesesOK = datosMes.filter(d => d.logC?.estado === 'completado' || d.logV?.estado === 'completado' || d.logHono?.estado === 'completado' || d.logBte?.estado === 'completado').length;
  const mesesError = datosMes.filter(d => d.logC?.estado === 'error' || d.logV?.estado === 'error' || d.logHono?.estado === 'error' || d.logBte?.estado === 'error').length;
  const mesesEjecutados = datosMes.filter(d => d.logC || d.logV || d.logHono || d.logBte).length;

  const dataGrafico = datosMes.map(d => ({
    mes: d.nombre,
    Ventas: d.totVentas,
    Compras: d.totCompras,
    Honorarios: d.totHono,
    BTE: d.totBte,
  }));

  const TABS = [
    { id: 'resumen', label: '📊 Resumen Anual' },
    { id: 'meses', label: '📅 Detalle por Mes' },
    { id: 'batch', label: '⚡ Ejecución Batch' },
  ];

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto min-h-screen bg-gray-50/50">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors flex-shrink-0">
          ← Volver
        </button>
        <span className="text-gray-300 hidden sm:inline">/</span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-800 truncate">{empresa.nombre}</h1>
          <p className="text-xs text-gray-400 font-mono">{empresa.rut}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <select value={anio} onChange={e => setAnio(Number(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
            {años.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={() => cargarDatos()} title="Actualizar"
            className="px-3 py-1.5 border rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-colors bg-white shadow-sm">
            ↻
          </button>
        </div>
      </div>

      {/* Tabs principales */}
      <div className="flex gap-0.5 border-b mb-5 overflow-x-auto bg-white rounded-t-xl px-2 pt-2 shadow-sm">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTabActiva(t.id)}
            className={`px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tabActiva === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-32 text-center text-gray-400">
          <div className="text-4xl mb-3 animate-spin inline-block">⟳</div>
          <p>Cargando información de {anio}...</p>
        </div>
      ) : (
        <>
          {/* ── TAB: Resumen Anual ── */}
          {tabActiva === 'resumen' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Total Ventas Año', value: fmtCLP(totalVentasAnio), color: 'blue', sub: 'acumulado' },
                  { label: 'Total Compras Año', value: fmtCLP(totalComprasAnio), color: 'green', sub: 'acumulado' },
                  { label: 'Total Honorarios', value: fmtCLP(totalHonoAnio), color: 'purple', sub: 'acumulado' },
                  { label: 'Total BTE', value: fmtCLP(totalBteAnio), color: 'teal', sub: 'acumulado' },
                  { label: 'Meses Ejecutados', value: mesesEjecutados, color: 'slate', sub: 'de 12 meses' },
                  { label: 'Meses Completados', value: mesesOK, color: 'green', sub: 'con al menos 1 OK' },
                  { label: 'Meses con Error', value: mesesError, color: mesesError > 0 ? 'red' : 'green', sub: mesesError > 0 ? 'requieren atención' : 'sin errores' },
                ].map((kpi, i) => (
                  <div key={i} className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">{kpi.label}</div>
                    <div className={`text-xl font-bold ${
                      kpi.color === 'blue' ? 'text-blue-700' :
                      kpi.color === 'green' ? 'text-green-700' :
                      kpi.color === 'purple' ? 'text-purple-700' :
                      kpi.color === 'teal' ? 'text-teal-700' :
                      kpi.color === 'red' ? 'text-red-600' :
                      kpi.color === 'slate' ? 'text-slate-700' : 'text-gray-800'
                    }`}>{kpi.value}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white border rounded-xl p-4 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-700 mb-4">Evolución Monetaria {anio} (CLP)</h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dataGrafico} margin={{ top: 5, right: 10, left: 30, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="mes" fontSize={12} tick={{ fill: '#6b7280' }} />
                      <YAxis tickFormatter={v => '$' + (v / 1000000).toFixed(1) + 'M'} fontSize={11} tick={{ fill: '#9ca3af' }} />
                      <Tooltip
                        formatter={(val) => fmtCLP(val)}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Compras" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Honorarios" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="BTE" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border rounded-xl p-4 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Vista rápida por mes</h2>
                <SelectorMeses
                  datosMes={datosMes}
                  mesSeleccionado={mesSeleccionado}
                  onSelect={(m) => { setMesSeleccionado(m); setTabActiva('meses'); }}
                />
              </div>
            </div>
          )}

          {/* ── TAB: Detalle por Mes ── */}
          {tabActiva === 'meses' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <SelectorMeses
                  datosMes={datosMes}
                  mesSeleccionado={mesSeleccionado}
                  onSelect={setMesSeleccionado}
                />
              </div>

              <div className="flex justify-end">
                <label className="flex items-center gap-1.5 cursor-pointer bg-white hover:bg-slate-50 border border-slate-300 px-3 py-1.5 rounded-lg transition-colors shadow-sm">
                  <input type="checkbox" checked={chkSoloFetch} onChange={e => {
                    setChkSoloFetch(e.target.checked);
                    if (e.target.checked) setChkCentralizacion(false);
                  }} className="accent-slate-600" />
                  <span className="text-xs font-semibold text-slate-700">☁️ Solo Descargar (Aplica a Re-ejecutar)</span>
                </label>
              </div>

              {mesActual && (
                <DetalleMes
                  d={mesActual}
                  descargandoArchivo={descargandoArchivo}
                  onDescargar={descargarArchivo}
                  onReejecutar={reejecutarProceso}
                  onCambiarEstado={cambiarEstadoLog}
                />
              )}
            </div>
          )}

          {/* ── TAB: Ejecución Batch ── */}
          {tabActiva === 'batch' && (
            <div className="bg-white border border-indigo-200 rounded-xl p-5 shadow-sm">
              <h3 className="font-bold text-indigo-800 mb-4">Ejecución Multi-Mes / Batch</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">1. Seleccionar Meses</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                      <button key={m} onClick={() => setMesesMulti(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m])}
                        className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors border ${
                          mesesMulti.includes(m) ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 border-gray-200'
                        }`}>
                        {MESES_SHORT[m]}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setMesesMulti([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Seleccionar todos</button>
                    <button onClick={() => setMesesMulti([])}
                      className="text-xs text-gray-400 hover:text-gray-600 font-medium">Limpiar</button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">2. Procesos a ejecutar</p>
                  <div className="grid grid-cols-2 gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input type="checkbox" checked={chkCompras} onChange={e => setChkCompras(e.target.checked)} className="rounded accent-emerald-600" /> Compras
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input type="checkbox" checked={chkVentas} onChange={e => setChkVentas(e.target.checked)} className="rounded accent-emerald-600" /> Ventas
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input type="checkbox" checked={chkHonorarios} onChange={e => setChkHonorarios(e.target.checked)} className="rounded accent-emerald-600" /> Honorarios
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input type="checkbox" checked={chkBte} onChange={e => setChkBte(e.target.checked)} className="rounded accent-emerald-600" /> BTE
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs col-span-2">
                      <input type="checkbox" checked={chkCentralizacion} onChange={e => setChkCentralizacion(e.target.checked)} className="rounded accent-emerald-600" disabled={chkSoloFetch} /> Centralización
                    </label>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-blue-800">
                      <input type="checkbox" checked={chkSoloFetch} onChange={e => {
                        setChkSoloFetch(e.target.checked);
                        if (e.target.checked) setChkCentralizacion(false);
                      }} className="rounded accent-blue-600" />
                      ☁️ Solo Descargar desde SII (No subir a Inputsoft)
                    </label>
                    <p className="text-[10px] text-blue-600 mt-1 ml-5 leading-tight">
                      Descarga y procesa el CSV sin enviarlo a Inputsoft. Útil para obtener estadísticas sin afectar la contabilidad.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-gray-100">
                <button onClick={ejecutarSeleccion} disabled={ejecutandoMulti || mesesMulti.length === 0 || (!chkCompras && !chkVentas && !chkHonorarios && !chkBte && !chkCentralizacion)}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50">
                  ▶ Ejecutar Selección ({mesesMulti.length} meses)
                </button>
                <button onClick={ejecutarTodoMulti} disabled={ejecutandoMulti || mesesMulti.length === 0}
                  className="flex-1 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50">
                  🌟 Ejecutar Todo ({mesesMulti.length} meses)
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
